import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormHelperText, Grid, Divider, Checkbox, Typography, Stack, Skeleton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { styles } from './styles';
import TextField from '../../../components/common/InputField/TextField';
import { useModifyUserActionMutation, useSaveUserMutation } from '../../../api/users';
import UserRoles from '../../roles/UserRoles';

const UserDetailsTab = ({ isEditMode, userDetails, isLoading }) => {
    const navigate = useNavigate();
    const {
        authorityList = [],
        companyList = [],
        companyRoles = {},
        secUserInstance = {},
        employeeInstance = {},
    } = userDetails || {};
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [modifyUserAction, { isLoading: isModifyLoading }] = useModifyUserActionMutation();
    const [saveUser, { isLoading: isSaveLoading }] = useSaveUserMutation();

    const loading = isModifyLoading || isSaveLoading;

    const handleCustomDetails = async (details) => {
        try {
            const email = (employeeInstance?.emailAddresses?.length && employeeInstance?.emailAddresses?.[0]) || '';

            const bodyFormData = new FormData();
            for (const key in details) {
                if (key === 'firstName') {
                    bodyFormData.append('employee.firstName', details['firstName']);
                } else if (key === 'lastName') {
                    bodyFormData.append('employee.lastName', details['lastName']);
                } else if (key === 'username') {
                    bodyFormData.append(
                        'username',
                        email === details['email'] ? details['username'] : details['email'],
                    );
                } else if (key === 'email') {
                    bodyFormData.append('employee.emailAddresses', details['email']);
                } else if (key === 'mobileNumber') {
                    bodyFormData.append('employee.mobileNumbers', details['mobileNumber']);
                } else if (key === 'enabled') {
                    bodyFormData.append('enabled', details['enabled'] ? 'on' : 'off');
                } else if (key === 'passwordExpired') {
                    bodyFormData.append('passwordExpired', details['passwordExpired'] ? 'on' : 'off');
                } else if (key === 'accountLocked') {
                    bodyFormData.append('accountLocked', details['accountLocked'] ? 'on' : 'off');
                } else if (key === 'sendCredentials') {
                    bodyFormData.append('sendCredentials', details['sendCredentials'] ? true : false);
                } else if (key === 'selectedCompanyRole') {
                    selectedCompanyRole.forEach((item) => {
                        bodyFormData.append(`${item.role}_N!N_${item.company}`, 'on');
                    });
                } else bodyFormData.append(key, details[key]);
            }

            bodyFormData.append('password', '123456789afAF');
            const api = isEditMode ? modifyUserAction : saveUser;
            if (!isEditMode) bodyFormData.delete('id');
            const response = await api(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response.message, { variant: 'success' });
            navigate(params.companyId ? `/company/${params.companyId}/users` : '/users');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const formik = useFormik({
        initialValues: {
            id: secUserInstance?.id ?? '',
            username: employeeInstance?.user?.username ?? '',
            lastName: employeeInstance?.lastName ?? '',
            firstName: employeeInstance?.firstName ?? '',
            email: (employeeInstance?.emailAddresses?.length && employeeInstance?.emailAddresses?.[0]) || '',
            mobileNumber: employeeInstance?.mobileNumbers?.[0] ?? '',
            enabled: secUserInstance?.enabled ?? false,
            accountLocked: secUserInstance?.accountLocked ?? false,
            passwordExpired: secUserInstance?.passwordExpired ?? false,
            sendCredentials: true,
            selectedCompanyRole:
                Object.keys(companyRoles).length > 0
                    ? Object.keys(companyRoles).map((key) => ({
                          company: key,
                          role: companyRoles[key],
                      }))
                    : [{ company: null, role: null }],
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            firstName: Yup.string()
                .max(255)
                .required('First Name is required')
                .matches(
                    /^[A-Za-z0-9 :,\-]+$/,
                    'First Name can contain only letters, numbers, spaces, commas, colon, and hyphens',
                ),
            lastName: Yup.string()
                .max(255)
                .required('Last Name is required')
                .matches(
                    /^[A-Za-z0-9 :,\-]+$/,
                    'Last Name can contain only letters, numbers, spaces, commas, colon, and hyphens',
                ),
            email: Yup.string().email('Invalid Email ID').required('Email ID is required'),
            mobileNumber: Yup.number()
                .typeError('Invalid Contact Number')
                .positive('Invalid Contact Number')
                .test('len', 'Invalid Contact Number', (value) => {
                    const length = value?.toString().length || 0;
                    return length === 0 || length === 10;
                })
                .optional(),
            selectedCompanyRole: Yup.array()
                .min(1, 'At least one company role is required')
                .of(
                    Yup.object().shape({
                        company: Yup.string()
                            .nullable()
                            .transform((value) => (value === null ? '' : value))
                            .required('Company is required'),
                        role: Yup.string()
                            .nullable()
                            .transform((value) => (value === null ? '' : value))
                            .required('Role is required'),
                    }),
                ),
        }),
        onSubmit: async (values, helpers) => {
            try {
                const initialValues = { ...values };
                handleCustomDetails(initialValues);
                // helpers.resetForm();
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });
    const { firstName, lastName, email, selectedCompanyRole } = formik.values;

    const isDisabled = useMemo(() => {
        return firstName === '' || lastName === '' || email === '' || selectedCompanyRole.length <= 0;
    }, [formik.values]);

    if (isLoading) {
        return (
            <Box sx={{ py: 4 }}>
                <Skeleton height={42} />
                <Skeleton />
                <Skeleton />
            </Box>
        );
    }

    return (
        <>
            <Typography mb={3}>User Details</Typography>
            <Grid container spacing={4}>
                <Grid item md={12} xs={12}>
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Grid container columnSpacing={6} rowSpacing={3}>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.email && formik.errors.email)}
                                        fullWidth
                                        helperText={formik.touched.email && formik.errors.email}
                                        label="Email ID"
                                        name="email"
                                        onBlur={formik.handleBlur}
                                        onChange={(e) => {
                                            const lowercased = e.target.value.toLowerCase();
                                            formik.setFieldValue('email', lowercased);
                                        }}
                                        value={formik.values.email}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                                        fullWidth
                                        helperText={formik.touched.firstName && formik.errors.firstName}
                                        label="First Name"
                                        name="firstName"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.firstName}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                                        fullWidth
                                        helperText={formik.touched.lastName && formik.errors.lastName}
                                        label="Last Name"
                                        name="lastName"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.lastName}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.mobileNumber && formik.errors.mobileNumber)}
                                        fullWidth
                                        helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                                        label="Contact Number"
                                        name="mobileNumber"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.mobileNumber}
                                    />
                                </Grid>
                            </Grid>
                            <Typography mt={3} mb={2}>
                                Account Settings
                            </Typography>
                            <Grid container spacing={4}>
                                <Grid item xs={12} sx={styles.checkboxWrapper}>
                                    <Grid item xs={4} sx={styles.checkbox}>
                                        <Checkbox
                                            color="secondary"
                                            checked={formik.values.enabled}
                                            name="enabled"
                                            disabled={false}
                                            onChange={(event) => formik.handleChange(event)}
                                        />
                                        <Typography color="textPrimary" sx={styles.typography}>
                                            Account Enabled
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4} sx={styles.checkbox}>
                                        <Checkbox
                                            color="secondary"
                                            checked={formik.values.accountLocked}
                                            name="accountLocked"
                                            disabled={false}
                                            onChange={(event) => formik.handleChange(event)}
                                        />
                                        <Typography color="textPrimary" sx={styles.typography}>
                                            Account Locked
                                        </Typography>
                                    </Grid>
                                    <Grid sx={styles.checkbox} item xs={4}>
                                        <Checkbox
                                            color="secondary"
                                            name="passwordExpired"
                                            checked={formik.values.passwordExpired}
                                            disabled={false}
                                            onChange={(event) => formik.handleChange(event)}
                                        />

                                        <Typography color="textPrimary" sx={styles.typography}>
                                            Password Expired
                                        </Typography>
                                    </Grid>
                                    <Grid sx={styles.checkbox} item xs={4}>
                                        <Checkbox
                                            color="secondary"
                                            name="sendCredentials"
                                            checked={formik.values.sendCredentials}
                                            disabled={false}
                                            onChange={(event) => formik.handleChange(event)}
                                        />
                                        <Typography color="textPrimary" sx={styles.typography}>
                                            Send Credentials
                                        </Typography>
                                    </Grid>
                                </Grid>
                                {formik.errors.submit && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                    </Grid>
                                )}
                                <Grid item xs={12} sx={styles.dividerWrapper}>
                                    <Divider item flexItem orientation="horizontal" sx={styles.divider} />{' '}
                                </Grid>
                                <UserRoles
                                    companies={companyList}
                                    roles={authorityList}
                                    selectedCompanyRole={formik.values.selectedCompanyRole}
                                    touched={formik.touched.selectedCompanyRole}
                                    errors={formik.errors.selectedCompanyRole}
                                    setSelectedCompanyRole={(values) =>
                                        formik.setFieldValue('selectedCompanyRole', values)
                                    }
                                />
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="end">
                                        <LoadingButton
                                            loading={loading}
                                            disabled={isDisabled || !formik.dirty}
                                            color="primary"
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default UserDetailsTab;
